import { environmentLabel, errorCategoryLabel, runOutcomeLabel } from 'lib';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { badge, fmtDate, fmtDuration, runOutcomeTone } from './helpers';

const th = 'text-left text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide px-3 py-2';
const td = 'px-3 py-2 text-[13px] text-[#353E4A] align-top';

const DetailRow = ({ label, children }) => (
  <div className="flex gap-2">
    <span className="text-[11px] font-semibold text-[#97A3B5] uppercase min-w-[150px]">
      {label}
    </span>
    <span className="text-[12px] text-[#353E4A] break-all">{children}</span>
  </div>
);

/** `{ fixturesUpdated: 4 }` reads as "fixturesUpdated 4" — what the job says it did. */
const Effect = ({ effect }) => {
  const entries = Object.entries(effect || {});
  if (!entries.length) return <span className="text-[#94A3B8]">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <span key={k} className={badge('info')}>
          {k} {v}
        </span>
      ))}
    </span>
  );
};

const RunsTable = ({ rows, loading, onTraceCalls }) => {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState(null);

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-[13px] text-[#97A3B5]">
        {t('admin.activityLog.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }

  if (!rows?.length) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-[13px] text-[#97A3B5]">
        {t('admin.activityLog.runs.empty', {
          defaultValue: 'No scheduled runs recorded for these filters.'
        })}
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-x-auto">
      <table className="w-full min-w-[980px] border-collapse">
        <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
          <tr>
            <th className={th}>{t('admin.activityLog.runs.job', { defaultValue: 'Job' })}</th>
            <th className={th}>
              {t('admin.activityLog.runs.scheduled', { defaultValue: 'Scheduled' })}
            </th>
            <th className={th}>
              {t('admin.activityLog.runs.started', { defaultValue: 'Started' })}
            </th>
            <th className={th}>{t('admin.activityLog.runs.took', { defaultValue: 'Took' })}</th>
            <th className={th}>
              {t('admin.activityLog.runs.outcome', { defaultValue: 'Outcome' })}
            </th>
            <th className={th}>{t('admin.activityLog.runs.effect', { defaultValue: 'Did' })}</th>
            <th className={th}>{t('admin.activityLog.runs.calls', { defaultValue: 'Calls' })}</th>
            <th className={th} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const open = openId === row._id;
            return (
              <Fragment key={row._id}>
                <tr className="border-b border-[#F1F5F9] hover:bg-[#FAFBFC]">
                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[12px]">{row.job}</span>
                      {row.registered === false && (
                        <span className={badge('warn')}>
                          {t('admin.activityLog.runs.unregistered', {
                            defaultValue: 'not declared'
                          })}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`${td} whitespace-nowrap`}>{fmtDate(row.scheduledAt)}</td>
                  <td className={`${td} whitespace-nowrap`}>{fmtDate(row.startedAt)}</td>
                  <td className={`${td} whitespace-nowrap`}>{fmtDuration(row.durationMs)}</td>
                  <td className={td}>
                    <span className={badge(runOutcomeTone(row.outcome))}>
                      {runOutcomeLabel(row.outcome)}
                    </span>
                  </td>
                  <td className={td}>
                    <Effect effect={row.effect} />
                  </td>
                  <td className={td}>
                    {row.callCount ? (
                      <button
                        type="button"
                        onClick={() => onTraceCalls?.(row._id)}
                        className="text-[12px] text-[#2563EB] hover:underline"
                      >
                        {row.callCount}
                      </button>
                    ) : (
                      <span className="text-[#94A3B8]">0</span>
                    )}
                  </td>
                  <td className={td}>
                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : row._id)}
                      className="text-[12px] text-[#2563EB] hover:underline"
                    >
                      {open
                        ? t('admin.activityLog.hide', { defaultValue: 'Hide' })
                        : t('admin.activityLog.details', { defaultValue: 'Details' })}
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr className="border-b border-[#F1F5F9] bg-[#FAFBFC]">
                    <td className={td} colSpan={8}>
                      <div className="flex flex-col gap-1 py-1">
                        <DetailRow
                          label={t('admin.activityLog.detail.env', { defaultValue: 'Environment' })}
                        >
                          {environmentLabel(row.environment)}
                        </DetailRow>
                        <DetailRow
                          label={t('admin.activityLog.detail.instance', {
                            defaultValue: 'Ran on instance'
                          })}
                        >
                          {row.instanceId || '—'}
                        </DetailRow>
                        <DetailRow
                          label={t('admin.activityLog.detail.release', { defaultValue: 'Release' })}
                        >
                          {row.release || '—'}
                        </DetailRow>
                        <DetailRow
                          label={t('admin.activityLog.detail.finished', {
                            defaultValue: 'Finished'
                          })}
                        >
                          {fmtDate(row.finishedAt)}
                        </DetailRow>
                        {row.outcome === 'failed' && (
                          <>
                            <DetailRow
                              label={t('admin.activityLog.detail.errorCategory', {
                                defaultValue: 'Error category'
                              })}
                            >
                              {errorCategoryLabel(row.errorCategory)}
                            </DetailRow>
                            {row.errorSummary ? (
                              <DetailRow
                                label={t('admin.activityLog.detail.errorSummary', {
                                  defaultValue: 'Error detail'
                                })}
                              >
                                {row.errorSummary}
                              </DetailRow>
                            ) : null}
                            {/*
                              Blank is not "no". A job that threw without saying whether it had
                              already written something leaves this genuinely unknown, and an admin
                              deciding whether to re-run needs to be told that rather than guess.
                            */}
                            <DetailRow
                              label={t('admin.activityLog.detail.partial', {
                                defaultValue: 'Wrote before failing'
                              })}
                            >
                              {row.changesMadeBeforeFailure == null
                                ? t('admin.activityLog.detail.unknown', {
                                    defaultValue: 'Unknown — the job did not say'
                                  })
                                : String(row.changesMadeBeforeFailure)}
                            </DetailRow>
                          </>
                        )}
                        {row.outcome === 'indeterminate' && (
                          <DetailRow
                            label={t('admin.activityLog.detail.indeterminate', {
                              defaultValue: 'Why'
                            })}
                          >
                            {t('admin.activityLog.detail.indeterminateWhy', {
                              defaultValue:
                                'This run started and never reported an outcome — most likely the process was stopped mid-run. It may have completed its work.'
                            })}
                          </DetailRow>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RunsTable;
